import Button from 'components/Button';
import ComponentPlaceholder from 'components/ComponentPlaceholder';
import Icon from 'components/Icon';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import RichText from 'components/RichText';
import Status from 'components/Status';
import { useChat, useGlobalEvent } from 'hooks';
import type {
	Field,
	PartialPopoverProps,
	SitecoreBlockProps,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { useI18n } from 'utils/i18n';

interface Props extends SitecoreBlockProps {
	fields?: {
		chatBusyMessage: Field<string>;
		chatClosedMessage: Field<string>;
		heading: Field<string>;
	};
	partialProps?: PartialPopoverProps;
}

export default function ChatButton({ fields, params, partialProps }: Props) {
	const { t } = useI18n();
	const {
		activateChatWidget,
		chatHasAvailableAgents,
		chatIsOpen,
		isLoadingChatAvailability,
		isLoadingChatWidget,
		openChat,
	} = useChat(true);

	useGlobalEvent('chat-widget-ready', () => {
		partialProps?.onPopoverClose();
		openChat();
	});

	if (!fields) {
		return null;
	}

	const heading = fields.heading?.value;
	const chatBusyMessage = fields.chatBusyMessage?.value;
	const chatClosedMessage = fields.chatClosedMessage?.value;

	if (!heading || !chatBusyMessage || !chatClosedMessage) {
		return (
			<ComponentPlaceholder
				className={getEditorMargin(params)}
				componentName="ChatButton"
				description="Requires a heading"
			/>
		);
	}

	return (
		<div className={getEditorMargin(params)}>
			{isLoadingChatAvailability && (
				<LoadingSkeleton>
					<LoadingSkeletonItem height="3rem" />
				</LoadingSkeleton>
			)}
			{!isLoadingChatAvailability && (
				<>
					{chatIsOpen && chatHasAvailableAgents && (
						<Button
							variant="cta"
							displayWidth="full"
							disabled={isLoadingChatWidget}
							onClick={activateChatWidget}
						>
							{isLoadingChatWidget && <Icon name="loading" />}
							<span className={isLoadingChatWidget ? 'sr-only' : undefined}>
								{isLoadingChatWidget ? t('general_loading_text') : heading}
							</span>
						</Button>
					)}
					{chatIsOpen && !chatHasAvailableAgents && (
						<Status
							variant="negative"
							text={<RichText html={chatBusyMessage} />}
						/>
					)}
					{!chatIsOpen && (
						<Status
							variant="negative"
							text={<RichText html={chatClosedMessage} />}
						/>
					)}
				</>
			)}
		</div>
	);
}
ChatButton.displayName = 'ChatButton_Partial';
